import Highcharts, { type Options } from "highcharts"
import { groupBy } from "lodash-es"

export const chartOptions: Options = {
  chart: {
    backgroundColor: "none",
    height: 235,
    marginLeft: 70,
    style: {
      fontFamily:
        '"Roboto", BlinkMacSystemFont, -apple-system, "Segoe UI", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif'
    }
  },
  credits: {
    enabled: false
  },
  legend: {
    enabled: false
  },
  time: {
    useUTC: false
  },
  title: {
    text: ""
  },
  xAxis: {
    crosshair: {
      snap: false
    },
    labels: {
      formatter(ctx) {
        const dateTimeLabelFormat = ctx.dateTimeLabelFormat ?? ""
        const formattedDate = new Highcharts.Time({ useUTC: false }).dateFormat(
          dateTimeLabelFormat,
          ctx.pos
        )
        if (dateTimeLabelFormat === "%e. %b") {
          return `<span class="font-medium">${formattedDate}</span>`
        }
        return formattedDate
      }
    },
    showEmpty: true,
    title: {
      text: undefined
    },
    type: "datetime"
  },
  yAxis: {
    min: 0,
    showEmpty: true,
    title: {
      text: undefined
    }
  }
}

export const CDN_COLORS = [
  "var(--color-teal-600)",
  "var(--color-purple-700)",
  "var(--color-pink-500)",
  "var(--color-orange-700)",
  "var(--color-teal-800)"
] as const

const sortCdns = (data: [string, [number, number][]][]) =>
  data.sort((a, b) => {
    const [, aData] = a
    const [, bData] = b
    const totalViewerA = aData.reduce((acc, [, viewers]) => acc + viewers, 0)
    const totalViewerB = bData.reduce((acc, [, viewers]) => acc + viewers, 0)
    return totalViewerB - totalViewerA
  })

const reduceCdnsPerTimestamps = (data: [string, [number, number][]][]) => {
  const withoutNames = data
    .flatMap(([, viewersData]) => viewersData)
    .map(([timestamp, viewers]) => ({ timestamp, viewers }))

  const reducedByTimestamps = Object.entries(groupBy(withoutNames, "timestamp")).map(
    ([timestamp, viewersData]) => {
      const totalViewers = viewersData.reduce((acc, { viewers }) => acc + viewers, 0)
      return [+timestamp, totalViewers] as [number, number]
    }
  )
  return reducedByTimestamps
}

export const getClosestRoundedTimestamp = (timestamp: number, duration: number) => {
  const interval = getStepFromDuration(duration)

  if (duration >= 2 * 24 * 60 * 60 * 1000) {
    // 2 days
    const date = new Date(timestamp)
    date.setHours(0, 0, 0, 0)
    return date.getTime()
  }

  return Math.ceil(timestamp / interval) * interval
}

export const getStepFromDuration = (duration: number) => {
  if (duration >= 7 * 24 * 60 * 60 * 1000 /* 7 days */) return 24 * 60 * 60 * 1000 // 1 day
  if (duration >= 4 * 24 * 60 * 60 * 1000 /* 4 days */) return 12 * 60 * 60 * 1000 // 12 hours
  if (duration >= 24 * 60 * 60 * 1000 /* 1 day */) return 4 * 60 * 60 * 1000 // 4 hours
  if (duration >= 12 * 60 * 60 * 1000 /* 12 hours */) return 3 * 60 * 60 * 1000 // 3 hours
  if (duration >= 6 * 60 * 60 * 1000 /* 6 hours */) return 2 * 60 * 60 * 1000 // 2 hours
  if (duration >= 4 * 60 * 60 * 1000 /* 4 hours */) return 1 * 60 * 60 * 1000 // 1 hour
  if (duration >= 2 * 60 * 60 * 1000 /* 2 hours */) return 30 * 60 * 1000 // 30 min
  if (duration >= 60 * 60 * 1000 /* 1 hour */) return 10 * 60 * 1000 // 10 min
  if (duration >= 15 * 60 * 1000 /* 15 minutes */) return 1 * 60 * 1000 // 1 min
  if (duration >= 30 * 60 * 1000 /* 30 minutes */) return 2 * 60 * 1000 // 2 min
  return Math.max(Math.floor(duration / 6), 1) // fallback: 6 points
}

export function computeChartSeries<T extends { timestamp: number }>(
  statistics: T[],
  getValue: (item: T) => number
) {
  return {
    data: statistics.map(stat => [stat.timestamp * 1000, getValue(stat)] as [number, number]) ?? []
  }
}

export function computeChartSeriesByCdn<
  T extends { timestamp: number; repartition: K[] },
  K extends { cdnName: string }
>(statistics: T[], getValue: (item: K) => number) {
  const cdnList: Map<string, [number, number][]> = new Map()

  for (const { timestamp, repartition } of statistics) {
    for (const item of repartition) {
      cdnList.set(item.cdnName, [
        ...(cdnList.get(item.cdnName) || []),
        [timestamp * 1000, getValue(item)]
      ])
    }
  }

  const allEntries = sortCdns([...cdnList.entries()])
  const isGroupingLastSeries = cdnList.size > 5
  const shownEntries = isGroupingLastSeries
    ? [
        ...allEntries.slice(0, 3),
        ["Others", reduceCdnsPerTimestamps(allEntries.slice(3))] as [string, [number, number][]]
      ]
    : [...cdnList.entries()].sort()

  return {
    isGroupingLastSeries,
    series: shownEntries.map(([name, data]) => ({
      data,
      name
    }))
  }
}
